import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

const ENDED = ["hangup", "destroy", "purge"];
const TAB = Math.random().toString(36).slice(2);
const LINE = "tx-line-owner";

/* Browser phone line. Only the most recently opened dialer (this tab/page) holds the line and gets incoming calls. */
export const useTelnyx = (enabled, onInboundLinked, where = "Call Center") => {
  const client = useRef(null);
  const pending = useRef(0);
  const [ready, setReady] = useState(false);
  const [incoming, setIncoming] = useState(null);
  const [live, setLive] = useState(null);
  const [muted, setMuted] = useState(false);
  const [held, setHeld] = useState(false);
  const [failed, setFailed] = useState(false);
  const [standby, setStandby] = useState("");
  const [gen, setGen] = useState(0);
  useEffect(() => {
    if (!enabled) return undefined;
    localStorage.setItem(LINE, JSON.stringify({ tab: TAB, where, at: Date.now() }));
    setStandby("");
    const onStore = (e) => {
      if (e.key !== LINE || !e.newValue) return;
      const v = JSON.parse(e.newValue);
      if (v.tab !== TAB) { client.current?.disconnect(); client.current = null; setReady(false); setStandby(v.where || "another tab"); }
    };
    window.addEventListener("storage", onStore);
    let c, gone = false;
    (async () => {
      try {
        const { TelnyxRTC } = await import("@telnyx/webrtc");
        const { data } = await api.get("/api/callcenter/telnyx/token");
        if (gone) return;
        c = new TelnyxRTC({ login_token: data.login_token });
        c.remoteElement = "tx-remote-audio";
        c.on("telnyx.ready", () => !gone && setReady(true));
        c.on("telnyx.error", () => setReady(false));
        c.on("telnyx.socket.close", () => setReady(false));
        c.on("telnyx.notification", (n) => {
          if (n.type !== "callUpdate") return;
          const call = n.call;
          if (call.state === "ringing" && call.direction === "inbound") {
            if (Date.now() - pending.current < 30000) { pending.current = 0; call.answer(); setLive(call); }
            else setIncoming(call);
          }
          if (ENDED.includes(call.state)) { setIncoming(x => (x === call ? null : x)); setLive(x => (x === call ? null : x)); setMuted(false); setHeld(false); }
        });
        c.connect(); client.current = c;
      } catch { setFailed(true); toast.error("Browser calling unavailable — ask an admin to check the Telnyx setup. You can still call from your phone."); }
    })();
    return () => { gone = true; window.removeEventListener("storage", onStore); c?.disconnect(); };
  }, [enabled, gen, where]);
  const dial = async (callId) => { pending.current = Date.now(); await api.post("/api/callcenter/telnyx/dial", { call_id: callId }); };
  const answer = () => {
    incoming.answer(); setLive(incoming); setIncoming(null);
    setTimeout(() => api.get("/api/callcenter/telnyx/my-call").then(r => r.data && onInboundLinked?.(r.data)).catch(() => {}), 2000);
  };
  const mute = () => { live?.toggleAudioMute(); setMuted(m => !m); };
  const hold = async (callId) => {
    const next = !held;
    try { if (callId) await api.post(`/api/pd/calls/${callId}/control`, { action: next ? "hold" : "unhold" }); setHeld(next); }
    catch { toast.error("Couldn't change hold"); }
  };
  return { ready, failed, incoming, live, muted, held, standby, claim: () => setGen(g => g + 1), dial, answer, mute, hold, dtmf: (k) => live?.dtmf?.(k), hang: () => live?.hangup(), reject: () => { incoming?.hangup(); setIncoming(null); } };
};
